import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  RoofMaterialTypeDetailResponse,
  RoofMaterialTypeForm,
  RoofMaterialTypeListResponse,
  RoofMaterialTypeUpdate,
} from "../types";
import {
  selectRoofMaterialTypeForm,
  selectRoofMaterialTypePage,
  selectRoofMaterialTypePerPage,
} from "./selector";
import { roofMaterialTypeActions } from "./slice";

let isFetchingList = false;

export function* handleGetRoofMaterialTypeList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectRoofMaterialTypePage);
    const perPage: number = yield select(selectRoofMaterialTypePerPage);

    const response: RoofMaterialTypeListResponse = yield call(
      request,
      `${API_URL}/roof-material-type/list?page=${page}&perPage=${perPage}`,
      { method: "GET" }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(roofMaterialTypeActions.getRoofMaterialTypeListSuccess(payload));
    } else {
      yield put(roofMaterialTypeActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectRoofMaterialTypePage);

    if (page === 1) {
      yield put(roofMaterialTypeActions.getRoofMaterialTypeListError(message));
    } else {
      yield put(roofMaterialTypeActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddRoofMaterialType() {
  const form: RoofMaterialTypeForm = yield select(selectRoofMaterialTypeForm);

  try {
    yield call(request, `${API_URL}/roof-material-type`, {
      method: "POST",
      body: JSON.stringify({
        materialTypeDesc: form.materialTypeDesc.trim(),
      }),
    });

    yield put(roofMaterialTypeActions.addRoofMaterialTypeSuccess());
    yield put(roofMaterialTypeActions.getRoofMaterialTypeList());
  } catch (error) {
    yield put(
      roofMaterialTypeActions.addRoofMaterialTypeError(
        (error as Error).message
      )
    );
  }
}

export function* handleUpdateRoofMaterialType(
  action: PayloadAction<RoofMaterialTypeUpdate>
) {
  const { id, materialTypeDesc } = action.payload;

  try {
    yield call(request, `${API_URL}/roof-material-type/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        materialTypeDesc: materialTypeDesc.trim(),
      }),
    });

    yield put(roofMaterialTypeActions.updateRoofMaterialTypeSuccess());
    yield put(roofMaterialTypeActions.getRoofMaterialTypeList());
  } catch (error) {
    yield put(
      roofMaterialTypeActions.updateRoofMaterialTypeError(
        (error as Error).message
      )
    );
  }
}

export function* handleGetRoofMaterialTypeDetail(
  action: PayloadAction<number>
) {
  const id = action.payload;

  try {
    const response: RoofMaterialTypeDetailResponse = yield call(
      request,
      `${API_URL}/roof-material-type/${id}`,
      { method: "GET" }
    );

    yield put(
      roofMaterialTypeActions.getRoofMaterialTypeDetailSuccess(response?.data)
    );
  } catch (error) {
    yield put(
      roofMaterialTypeActions.getRoofMaterialTypeDetailError(
        (error as Error).message
      )
    );
  }
}

export function* roofMaterialTypeSaga() {
  yield takeLeading(
    [
      roofMaterialTypeActions.getRoofMaterialTypeList.type,
      roofMaterialTypeActions.getNextPageItems.type,
    ],
    handleGetRoofMaterialTypeList
  );
  yield takeLatest(
    roofMaterialTypeActions.addRoofMaterialType.type,
    handleAddRoofMaterialType
  );
  yield takeLatest(
    roofMaterialTypeActions.updateRoofMaterialType.type,
    handleUpdateRoofMaterialType
  );
  yield takeLatest(
    roofMaterialTypeActions.getRoofMaterialTypeDetail.type,
    handleGetRoofMaterialTypeDetail
  );
}

export default roofMaterialTypeSaga;
